import "./list-reward-event.scss";

import TagColor from "@core/ui/tag-color/tag-color";
import { GetRewardEventModel } from "@factory/model/query-model-reward-event";
import { useQyGetRewardEvent } from "@factory/query/reward-event.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";
import { FilterRewardEvent } from "../filter-reward-event/filter-reward-event";
import { useFilterRewardEventContext } from "../filter-reward-event/filter-reward-event.context";

export function ListRewardEvent() {
  // THIS IS THE LOCAL DECLARATION
  const { pointsText, stampsText } = appFeature.config.label;
  const { formatDateLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterRewardEventContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: rewardEventResponse,
    isLoading,
    isError,
    error,
  } = useQyGetRewardEvent(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetRewardEventModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterRewardEvent />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{rewardEventResponse?.count}</b>
      </h5>

      <DataTable
        value={rewardEventResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="level_name" header="Level" sortable></Column>
        <Column field="name" header="Name" sortable></Column>
        <Column field="description" header="Description" sortable></Column>
        <Column
          body={(cell) => <TagColor value={cell.is_points} />}
          field="is_points"
          header={`Is ${pointsText}`}
          sortable
        ></Column>
        <Column
          body={(cell) => <TagColor value={cell.is_stamp} />}
          field="is_stamp"
          header={`Is ${stampsText}`}
          sortable
        ></Column>
        <Column
          field="denomination_points"
          header="Denomination Points"
          sortable
        ></Column>
        <Column field="points" header={pointsText} sortable></Column>
        <Column
          field="denomination_stamp"
          header="Denomination Stamp"
          sortable
        ></Column>
        <Column field="stamp" header={stampsText} sortable></Column>
        <Column
          body={(cell) => formatDateLocal(cell.start_date)}
          field="start_date"
          header="Start Date"
          sortable
        ></Column>
        <Column
          body={(cell) => formatDateLocal(cell.end_date)}
          field="end_date"
          header="End Date"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={rewardEventResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListRewardEvent" className="reward-event">
      <HeaderContent title={<h1>Reward Event</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListRewardEvent;
